import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Skeleton,
  Text,
  Flex,
  Badge,
  SimpleGrid,
  Stack,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BsPlus,
  BsEye,
  BsPencil,
  BsTrash,
  BsSearch,
  BsThreeDotsVertical,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";

const RequestList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, statusFilter, departmentFilter, priorityFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequests();
      // Handle response structure: { success, message, data, pagination }
      const requestsData = response?.data || [];
      console.log("📦 Fetched requests:", requestsData);
      setRequests(requestsData);
    } catch (error) {
      console.error("❌ Error fetching requests:", error);
      toast({
        title: "Error fetching requests",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (searchTerm) {
      filtered = filtered.filter(
        (req) =>
          req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((req) => req.status === statusFilter);
    }

    if (departmentFilter && departmentFilter !== "all") {
      filtered = filtered.filter((req) => req.department === departmentFilter);
    }

    if (priorityFilter && priorityFilter !== "all") {
      filtered = filtered.filter((req) => req.priority === priorityFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleCreateRequest = () => {
    navigate("/procurement/requests/create");
  };

  const handleViewRequest = (id) => {
    navigate(`/procurement/requests/${id}`);
  };

  const handleEditRequest = (id) => {
    navigate(`/procurement/requests/${id}/edit`);
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) {
      return;
    }

    try {
      await procurementApi.deleteRequest(id);
      toast({
        title: "Request deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error deleting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmitRequest = async (id) => {
    try {
      await procurementApi.submitRequest(id);
      toast({
        title: "Request submitted for approval",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleApproveRequest = async (id) => {
    try {
      await procurementApi.approveRequest(id, { comments: "Approved" });
      toast({
        title: "Request approved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error approving request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRejectRequest = async (id) => {
    const reason = window.prompt("Please provide a reason for rejection:");
    if (!reason) return;

    try {
      await procurementApi.rejectRequest(id, { reason });
      toast({
        title: "Request rejected",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error rejecting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size="lg">Procurement Requests</Heading>
        <Button
          leftIcon={<BsPlus />}
          colorScheme="blue"
          onClick={handleCreateRequest}
          w={{ base: "full", md: "auto" }}
        >
          Create Request
        </Button>
      </Flex>

      {/* Filters */}
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={3}
        mb={6}
        flexWrap="wrap"
      >
        <InputGroup maxW={{ base: "full", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="IN_REVIEW">In Review</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="SENT_BACK">Sent Back</option>
        </Select>

        <Select
          placeholder="All Departments"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          <option value="IT">IT</option>
          <option value="HR">HR</option>
          <option value="Finance">Finance</option>
          <option value="Operations">Operations</option>
          <option value="Marketing">Marketing</option>
          <option value="Sales">Sales</option>
        </Select>

        <Select
          placeholder="All Priorities"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          maxW={{ base: "full", md: "150px" }}
        >
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </Select>
      </Stack>

      {/* Mobile Cards */}
      <Box display={{ base: "block", md: "none" }}>
        {filteredRequests.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            textAlign="center"
          >
            <Text color="gray.500">No requests found</Text>
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={3}>
            {filteredRequests.map((request) => (
              <Box
                key={request._id || request.id}
                bg={bgColor}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <HStack justify="space-between" align="start" mb={2}>
                  <Box maxW="70%">
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      {request.requestId || request._id?.slice(-8) || "N/A"}
                    </Text>
                    <Text fontWeight="semibold" noOfLines={2}>
                      {request.title}
                    </Text>
                  </Box>
                  <StatusBadge status={request.status} />
                </HStack>

                <Divider my={2} />

                <SimpleGrid columns={2} spacing={2} mb={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Department
                    </Text>
                    <Text fontSize="sm">{request.department || "N/A"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Category
                    </Text>
                    <Text fontSize="sm">{request.category || "N/A"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Priority
                    </Text>
                    {request.priority ? (
                      <Badge
                        mt={1}
                        colorScheme={
                          request.priority === "URGENT"
                            ? "red"
                            : request.priority === "HIGH"
                              ? "orange"
                              : request.priority === "MEDIUM"
                                ? "blue"
                                : "gray"
                        }
                      >
                        {request.priority}
                      </Badge>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        N/A
                      </Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Created
                    </Text>
                    <Text fontSize="sm">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    leftIcon={<BsEye />}
                    variant="outline"
                    onClick={() => handleViewRequest(request._id || request.id)}
                  >
                    View
                  </Button>

                  {request.status === "DRAFT" && (
                    <>
                      <Button
                        size="sm"
                        leftIcon={<BsPencil />}
                        variant="outline"
                        onClick={() =>
                          handleEditRequest(request._id || request.id)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsCheckCircle />}
                        colorScheme="green"
                        variant="outline"
                        onClick={() =>
                          handleSubmitRequest(request._id || request.id)
                        }
                      >
                        Submit
                      </Button>
                    </>
                  )}

                  {(request.status === "SUBMITTED" ||
                    request.status === "IN_REVIEW") && (
                    <>
                      <Button
                        size="sm"
                        leftIcon={<BsCheckCircle />}
                        colorScheme="green"
                        variant="outline"
                        onClick={() =>
                          handleApproveRequest(request._id || request.id)
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsXCircle />}
                        colorScheme="red"
                        variant="outline"
                        onClick={() =>
                          handleRejectRequest(request._id || request.id)
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}

                  {request.status === "DRAFT" && (
                    <Button
                      size="sm"
                      leftIcon={<BsTrash />}
                      colorScheme="red"
                      variant="ghost"
                      onClick={() =>
                        handleDeleteRequest(request._id || request.id)
                      }
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Table */}
      <Box
        display={{ base: "none", md: "block" }}
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Request ID</Th>
              <Th>Title</Th>
              <Th>Department</Th>
              <Th>Category</Th>
              <Th>Priority</Th>
              <Th>Status</Th>
              <Th>Created Date</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRequests.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.500">No requests found</Text>
                </Td>
              </Tr>
            ) : (
              filteredRequests.map((request) => (
                <Tr key={request._id || request.id}>
                  <Td fontWeight="semibold">
                    {request.requestId || request._id?.slice(-8) || "N/A"}
                  </Td>
                  <Td maxW="250px" isTruncated>
                    {request.title}
                  </Td>
                  <Td>{request.department || "N/A"}</Td>
                  <Td>{request.category || "N/A"}</Td>
                  <Td>
                    {request.priority ? (
                      <Badge
                        colorScheme={
                          request.priority === "URGENT"
                            ? "red"
                            : request.priority === "HIGH"
                              ? "orange"
                              : request.priority === "MEDIUM"
                                ? "blue"
                                : "gray"
                        }
                      >
                        {request.priority}
                      </Badge>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        N/A
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={request.status} />
                  </Td>
                  <Td>{new Date(request.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        size="sm"
                        icon={<BsEye />}
                        variant="ghost"
                        onClick={() =>
                          handleViewRequest(request._id || request.id)
                        }
                        aria-label="View request"
                      />
                      {request.status === "DRAFT" && (
                        <IconButton
                          size="sm"
                          icon={<BsPencil />}
                          variant="ghost"
                          onClick={() =>
                            handleEditRequest(request._id || request.id)
                          }
                          aria-label="Edit request"
                        />
                      )}
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<BsThreeDotsVertical />}
                          size="sm"
                          variant="ghost"
                        />
                        <MenuList>
                          {request.status === "DRAFT" && (
                            <>
                              <MenuItem
                                icon={<BsCheckCircle />}
                                onClick={() =>
                                  handleSubmitRequest(request._id || request.id)
                                }
                              >
                                Submit for Approval
                              </MenuItem>
                              <MenuItem
                                icon={<BsTrash />}
                                onClick={() =>
                                  handleDeleteRequest(request._id || request.id)
                                }
                                color="red.500"
                              >
                                Delete
                              </MenuItem>
                            </>
                          )}
                          {(request.status === "SUBMITTED" ||
                            request.status === "IN_REVIEW") && (
                            <>
                              <MenuItem
                                icon={<BsCheckCircle />}
                                onClick={() =>
                                  handleApproveRequest(
                                    request._id || request.id,
                                  )
                                }
                              >
                                Approve
                              </MenuItem>
                              <MenuItem
                                icon={<BsXCircle />}
                                onClick={() =>
                                  handleRejectRequest(request._id || request.id)
                                }
                                color="red.500"
                              >
                                Reject
                              </MenuItem>
                            </>
                          )}
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Summary */}
      <Flex justify="space-between" align="center" mt={4}>
        <Text color="gray.600">
          Showing {filteredRequests.length} of {requests.length} requests
        </Text>
      </Flex>
    </Box>
  );
};

export default RequestList;
